import React, { useState, useEffect, useRef } from 'react';
import '../App.css';
import { useTheme } from '../ThemeContext';
import { fetchWeatherStories, selectTopStory } from '../lib/nwsWeatherStories';
import OfficeMapSVG from './OfficeMapSVG';

const WEATHER_PARAMS = [
    // Temperature
    { id: 'maxt', name: 'Max Temperature', unit: '(°F)', layerSuffix: 'maxt', fullname: 'Maximum Temperature' },
    { id: 'mint', name: 'Min Temperature', unit: '(°F)', layerSuffix: 'mint', fullname: 'Minimum Temperature' },
    // Wind
    { id: 'windgust', name: 'Wind Gust', unit: '(kts)', layerSuffix: 'windgust', fullname: 'Wind Gust' },
    { id: 'winddir', name: 'Wind Direction', unit: '', layerSuffix: 'winddir', fullname: 'Wind Direction' },
    // Precipitation
    { id: 'pop12', name: 'Precipitation Prob (12hr)', unit: '(%)', layerSuffix: 'pop12', fullname: 'Probability of Precipitation (12hr)' },
    { id: 'prob_precip_ext', name: 'Probabilistic Precipitation Portal↗', externalUrl: 'https://www.wpc.ncep.noaa.gov/Prob_Precip/' },
    // Snow (hidden for now)
    // { id: 'snowamt', name: 'Snow Amount', unit: '(in)', layerSuffix: 'snowamt', fullname: 'Snow Amount' },
];

const API_URL = process.env.REACT_APP_API_URL;

const WeatherImage = ({ src, alt, label, legendSrc, variant = 'main' }) => {
    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Reset error when src changes
    useEffect(() => {
        setHasError(false);
        setIsLoading(true);
    }, [src]);

    const isSidePanel = variant === 'side';
    const legendAlignment = isSidePanel ? 'start-0' : 'end-0';
    const legendMaxWidth = isSidePanel ? '100%' : '50%';

    const imageSizeStyles = isSidePanel
        ? {
            maxWidth: '90%',
            maxHeight: '90%',
            width: '100%',
            height: 'auto',
        }
        : {
            maxWidth: '95%',
            maxHeight: '95%',
            width: '100%',
            height: 'auto',
        };

    return (
        <div className="position-relative w-100 h-100 bg-secondary bg-opacity-10 overflow-hidden">
            <div className="position-absolute top-0 start-0 p-2 text-muted fw-bold small" style={{ zIndex: 10, textShadow: '0 0 2px black' }}>{label}</div>

            {isLoading && !hasError && (
                <div className="position-absolute top-50 start-50 translate-middle text-muted">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    <span className="small">Loading...</span>
                </div>
            )}

            {!hasError ? (
                <>
                    <div className="d-flex align-items-center justify-content-center w-100 h-100">
                        <img
                            src={src}
                            alt={alt}
                            style={{
                                objectFit: 'contain',
                                opacity: isLoading ? 0.5 : 1,
                                transition: 'opacity 0.3s',
                                ...imageSizeStyles,
                            }}
                            onLoad={() => setIsLoading(false)}
                            onError={() => {
                                setIsLoading(false);
                                setHasError(true);
                            }}
                        />
                    </div>
                    {legendSrc && !isLoading && (
                        <div className={`position-absolute bottom-0 p-2 ${legendAlignment}`}
                            style={{ maxWidth: legendMaxWidth, maxHeight: '80%', pointerEvents: 'none' }}>
                            <img src={legendSrc} alt="Legend" className="img-fluid rounded shadow-sm bg-white bg-opacity-75"
                            />
                        </div>
                    )}
                </>
            ) : (
                <div className="d-flex align-items-center justify-content-center w-100 h-100 text-secondary p-3 text-center">
                    <div>
                        <i className="fas fa-eye-slash mb-2 fa-2x opacity-50"></i><br />
                        <span className="small">Map Unavailable</span>
                    </div>
                </div>
            )}
        </div>
    );
};

const FutureWeather = ({ selectedDate, timezone }) => {
    const [selectedParam, setSelectedParam] = useState('maxt');
    const currentParam = WEATHER_PARAMS.find(p => p.id === selectedParam);
    const { theme } = useTheme();
    const isDarkMode = theme === 'dark';

    // Weather Stories state
    const [activeView, setActiveView] = useState('forecast');     // 'forecast' | 'stories'
    const [selectedOffice, setSelectedOffice] = useState(null);   // { id, name, lat, lon }
    const [storyData, setStoryData] = useState(null);             // { title, imageUrl } | null
    const [isStoryLoading, setIsStoryLoading] = useState(false);
    const [storyError, setStoryError] = useState(null);           // error message string | null
    const storyCache = useRef({});                                // session cache

    // Construct WMS URL
    const getWmsImageUrl = (region) => {
        const baseUrl = API_URL ? API_URL.replace(/\/$/, '') : '';
        const vtit = `${selectedDate}T00:00`; // add ${selectedDate} later
        const layerName = `ndfd.${region}.${selectedParam},states,counties`;

        const dimensions = {
            conus: { width: '1024', height: '576' },
            alaska: { width: '800', height: '560' },
            hawaii: { width: '800', height: '640' }
        };

        const { width, height } = dimensions[region] || dimensions.conus;

        // Construct query parameters
        const isTemperature = selectedParam === 'maxt' || selectedParam === 'mint';
        const params = new URLSearchParams({
            SERVICE: 'WMS',
            VERSION: '1.3.0',
            REQUEST: 'GetMap',
            LAYERS: layerName,
            WIDTH: width,
            HEIGHT: height,
            CRS: 'EPSG:3857',
            FORMAT: 'image/png',
            TRANSPARENT: 'TRUE',
            VTIT: vtit,
            ...(timezone && { TIMEZONE: timezone }),
            ...(isTemperature && { SEASON: '1' }),
        });

        const bboxes = {
            conus: '-14000000,2500000,-7000000,6500000',  // Tweaked CONUS BBOX for better fit
            alaska: '-20000000,6000000,-14000000,11500000',
            hawaii: '-18000000,1900000,-17000000,2600000'
        };

        params.append('STYLES', '');
        params.append('BBOX', bboxes[region] || bboxes.conus);

        return `${baseUrl}/wms/ndfd?${params.toString()}`;
    };

    const getLegendUrl = (region) => {
        const baseUrl = API_URL ? API_URL.replace(/\/$/, '') : '';
        const isTemperature = selectedParam === 'maxt' || selectedParam === 'mint';
        const seasonParam = isTemperature ? '&season=1' : '';
        return `${baseUrl}/wms/legend?region=${region}&parameter=${selectedParam}${seasonParam}`;
    };

    const handleOfficeClick = async (office) => {
        setSelectedOffice(office);
        setStoryError(null);
        setStoryData(null);

        // Check cache first
        if (storyCache.current[office.id]) {
            const cached = storyCache.current[office.id];
            const stories = cached.stories || cached['@graph'] || [];
            const topStory = selectTopStory(stories);
            if (topStory) {
                setStoryData({
                    title: topStory.title || 'Weather Story',
                    imageUrl: topStory.download || '',
                });
            } else {
                setStoryError('No story available for this office.');
            }
            return;
        }

        // Fetch from API
        setIsStoryLoading(true);
        try {
            const response = await fetchWeatherStories(office.id);
            storyCache.current[office.id] = response; // Cache it

            const stories = response.stories || response['@graph'] || [];
            const topStory = selectTopStory(stories);
            if (topStory) {
                setStoryData({
                    title: topStory.title || 'Weather Story',
                    imageUrl: topStory.download || '',
                });
            } else {
                setStoryError('No story available for this office.');
            }
        } catch (err) {
            setStoryError('Could not load weather stories. Please try again.');
        } finally {
            setIsStoryLoading(false);
        }
    };

    return (
        <div className="card shadow-sm h-100 border-secondary future-weather-card">
            <div className="card-header bg-transparent border-secondary d-flex justify-content-between align-items-center future-weather-header">
                <div className="btn-group btn-group-sm" role="group" aria-label="View toggle">
                    <button
                        type="button"
                        className={`btn btn-outline-secondary${activeView === 'forecast' ? ' active' : ''}`}
                        onClick={() => setActiveView('forecast')}
                    >
                        <i className="fas fa-cloud-sun me-1"></i>Weather Forecast
                    </button>
                    <button
                        type="button"
                        className={`btn btn-outline-secondary${activeView === 'stories' ? ' active' : ''}`}
                        onClick={() => {
                            setActiveView('stories');
                            setStoryData(null);
                            setSelectedOffice(null);
                        }}
                    >
                        <i className="fas fa-cloud-sun me-1"></i>Weather Stories
                    </button>
                </div>
                {activeView === 'forecast' && (
                    <select
                        className={`form-select form-select-sm border-secondary future-weather-select${isDarkMode ? ' bg-dark text-light' : ''}`}
                        style={{ backgroundColor: 'var(--bg-dropdown)', color: 'var(--text-main)' }}
                        value={selectedParam}
                        onChange={(e) => {
                            const chosen = WEATHER_PARAMS.find(p => p.id === e.target.value);
                            if (chosen?.externalUrl) {
                                window.open(chosen.externalUrl, '_blank', 'noopener,noreferrer');
                                e.target.value = selectedParam;
                            } else {
                                setSelectedParam(e.target.value);
                            }
                        }}
                    >
                        {WEATHER_PARAMS.map(param => (
                            <option key={param.id} value={param.id}>
                                {param.name}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            <div className="card-body p-0 position-relative future-weather-body">
                {activeView === 'stories' && (storyData || storyError) && (
                    <div className="d-flex align-items-center p-2 border-bottom border-secondary">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary me-2"
                            onClick={() => { setStoryData(null); setSelectedOffice(null); setStoryError(null); }}
                        >
                            <i className="fas fa-arrow-left me-1"></i>Back
                        </button>
                        {storyData && <small className="text-main fw-bold text-truncate">{selectedOffice?.name}: {storyData.title}</small>}
                    </div>
                )}

                {activeView === 'forecast' && (
                    <div className="d-flex h-100 flex-column future-weather-layout">
                        {/* CONUS - Main Area */}
                        <div className="position-relative border-bottom border-secondary w-100 future-weather-conus-panel">
                            <WeatherImage
                                src={getWmsImageUrl('conus')}
                                label="CONUS"
                                alt="Continental US Weather"
                                legendSrc={getLegendUrl('conus')}
                                variant="main"
                            />
                            {/* Overlay Info for Date */}
                            <div className="position-absolute bottom-0 start-0 p-3 future-weather-info">
                                <div className="p-2 rounded border border-secondary shadow" style={{ backgroundColor: 'var(--bg-hover)', color: 'var(--text-main)' }}>
                                    <div className="mb-1">
                                        <strong>{currentParam?.fullname}</strong> <span style={{ color: 'var(--text-muted)' }}>{currentParam?.unit}</span>
                                    </div>
                                    <small className="d-block border-top border-secondary pt-1" style={{ color: 'var(--text-muted)' }}>Forecast Valid For</small>
                                    <strong>{selectedDate}</strong>
                                </div>
                            </div>
                        </div>

                        {/* AK & HI - Bottom Row (stacks vertically on mobile) */}
                        <div className="d-flex flex-row w-100 future-weather-ak-hi-row">
                            {/* Alaska */}
                            <div className="position-relative border-end border-secondary future-weather-side-panel">
                                <WeatherImage
                                    src={getWmsImageUrl('alaska')}
                                    label="Alaska"
                                    alt="Alaska Weather"
                                    legendSrc={getLegendUrl('alaska')}
                                    variant="side"
                                />
                            </div>

                            {/* Hawaii */}
                            <div className="position-relative future-weather-side-panel">
                                <WeatherImage
                                    src={getWmsImageUrl('hawaii')}
                                    label="Hawaii"
                                    alt="Hawaii Weather"
                                    legendSrc={getLegendUrl('hawaii')}
                                    variant="side"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {activeView === 'stories' && !storyData && !isStoryLoading && !storyError && (
                    <OfficeMapSVG onOfficeClick={handleOfficeClick} theme={theme} />
                )}

                {activeView === 'stories' && isStoryLoading && (
                    <div className="d-flex align-items-center justify-content-center h-100 p-4">
                        <div className="spinner-border text-primary me-2" role="status"></div>
                        <span>Loading weather story...</span>
                    </div>
                )}

                {activeView === 'stories' && storyError && !storyData && (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100 p-4 text-center">
                        <i className="fas fa-info-circle fa-2x text-muted mb-2"></i>
                        <p className="text-muted mb-0">{storyError}</p>
                    </div>
                )}

                {activeView === 'stories' && storyData && (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100 p-2">
                        <div style={{ width: '75%', maxWidth: '1000px', height: 'auto' }}>
                            <img
                                src={storyData.imageUrl}
                                alt={storyData.title}
                                style={{ width: '100%', height: 'auto', objectFit: 'contain', borderRadius: '6px' }}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FutureWeather;
